import {
  formatFieldValue,
  isDateFormatKind,
  isNumericFormatKind,
  resolveDefaultFieldFormat,
  resolveFieldFormat,
  resolveMeasureKindFromName,
  roundHalfUp,
  toInsightValueFormat,
  type FieldFormat,
} from '../../lib/fieldFormat'
import type { AgentBuilderChartDataPoint, AgentBuilderKpiCard, DashboardValueFormat } from './types'

type DataRow = Record<string, string | number | null>

const DISPLAY_ACRONYMS = new Set(['AI', 'ATO', 'FYI', 'KPI', 'KPIS', 'LY', 'MYOB', 'WIP', 'XPM'])

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/
const SLASH_DATE_TIME_PATTERN = /^(\d{1,2}\/\d{1,2}\/\d{2,4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AP]M)?)?$/i
const TIME_SUFFIX_PATTERN = /\s+\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AP]M)?$/i

function formatDateAsMonthDayYear(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${day}/${year}`
}

function formatDateAsDayMonthYear(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = String(date.getFullYear() % 100).padStart(2, '0')
  return `${day}/${month}/${year}`
}

export function parseDashboardDate(value: string | number | null | undefined): Date | null {
  if (value === null || value === undefined || typeof value === 'number') return null
  const trimmed = String(value).trim()
  if (!trimmed) return null

  const slashMatch = trimmed.match(SLASH_DATE_TIME_PATTERN)
  if (slashMatch) {
    const [, slashDate] = slashMatch
    if (!slashDate) return null
    const [firstRaw, secondRaw, yearRaw] = slashDate.split('/')
    if (!firstRaw || !secondRaw || !yearRaw) return null
    const first = Number(firstRaw)
    const second = Number(secondRaw)
    const rawYear = Number(yearRaw)
    const year = rawYear < 100 ? 2000 + rawYear : rawYear
    const month = first > 12 ? second : first
    const day = first > 12 ? first : second
    const parsed = new Date(year, month - 1, day)
    if (
      Number.isNaN(parsed.getTime())
      || parsed.getFullYear() !== year
      || parsed.getMonth() !== month - 1
      || parsed.getDate() !== day
    ) {
      return null
    }
    return parsed
  }

  if (ISO_DATE_PATTERN.test(trimmed)) {
    const [datePart] = trimmed.split(/[T\s]/)
    const parts = datePart?.split('-').map(Number) ?? []
    if (parts.length >= 3 && parts.every(Number.isFinite)) {
      const [year, month, day] = parts
      if (year === undefined || month === undefined || day === undefined) return null
      const parsed = new Date(year, month - 1, day)
      if (
        Number.isNaN(parsed.getTime())
        || parsed.getFullYear() !== year
        || parsed.getMonth() !== month - 1
        || parsed.getDate() !== day
      ) {
        return null
      }
      return parsed
    }
  }

  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function isDateLike(value: string | number | null): boolean {
  return typeof value === 'string' && (ISO_DATE_PATTERN.test(value) || SLASH_DATE_TIME_PATTERN.test(value))
}

function isMeasureColumn(col: string, rows: DataRow[]): boolean {
  for (const row of rows) {
    const val = row[col]
    if (val !== null && val !== undefined) {
      return typeof val === 'number'
    }
  }
  return false
}

export function classifyColumns(columns: string[], rows: DataRow[]): { measureCols: string[]; dimensionCols: string[] } {
  const measureCols: string[] = []
  const dimensionCols: string[] = []
  for (const col of columns) {
    if (isMeasureColumn(col, rows)) {
      measureCols.push(col)
    } else {
      dimensionCols.push(col)
    }
  }
  return { measureCols, dimensionCols }
}

function getColValueType(col: string, rows: DataRow[]): 'string' | 'date' | 'number' | 'unknown' {
  for (const row of rows) {
    const val = row[col]
    if (val === null || val === undefined) continue
    if (typeof val === 'number') return 'number'
    if (typeof val === 'string') return isDateLike(val) ? 'date' : 'string'
  }
  return 'unknown'
}

export function pickLabelColumn(dimensionCols: string[], rows: DataRow[]): string | null {
  const priority = { string: 0, date: 1, number: 2, unknown: 3 } as const
  let best: string | null = null
  let bestPriority = Infinity
  for (const col of dimensionCols) {
    const p: number = priority[getColValueType(col, rows)]
    if (p < bestPriority) {
      bestPriority = p
      best = col
    }
  }
  return best
}

/** Display label for a date with no stored format: DD/MM/YY, matching the stored Date format. */
export function formatDateTimeLabel(value: string | number | null): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return String(value)
  const date = parseDashboardDate(value)
  if (date) return formatDateAsDayMonthYear(date)
  return value.replace(TIME_SUFFIX_PATTERN, '')
}

/**
 * Canonical chart point label (MM/DD/YYYY). Not for display: these labels are re-parsed by parseDashboardDate
 * (month-first for ambiguous slash dates) and sent as drilldown values, so they must keep this shape.
 */
export function formatChartDateKey(value: string | number | null): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return String(value)
  const date = parseDashboardDate(value)
  if (date) return formatDateAsMonthDayYear(date)
  return value.replace(TIME_SUFFIX_PATTERN, '')
}

export function formatTableCellString(value: string): string {
  if (isDateLike(value)) return formatDateTimeLabel(value)
  return value
}

export function formatTrendDateLabel(value: string | number | null, granularity: 'daily' | 'monthly' | 'yearly'): string {
  if (value === null || value === undefined) return ''
  const date = parseDashboardDate(value)
  if (!date) return String(value)

  if (granularity === 'yearly') return String(date.getFullYear())
  if (granularity === 'monthly') {
    return new Intl.DateTimeFormat('en-AU', { month: 'short', year: 'numeric' }).format(date)
  }

  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`
}

// Fixed names: Intl 'en-AU' short months are not stable across ICU builds (e.g. 'July', 'Sept').
const SHORT_MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/**
 * Short axis/tooltip label for chart categories: `1 Jul` (daily), `Jul 26` (monthly), `2026` (yearly).
 * Unparseable values pass through as text. Null/undefined give an empty label.
 */
export function formatShortDateLabel(value: string | number | null | undefined, granularity: 'daily' | 'monthly' | 'yearly'): string {
  if (value === null || value === undefined) return ''
  const date = parseDashboardDate(value)
  if (!date) return String(value)

  if (granularity === 'yearly') return String(date.getFullYear())
  const month = SHORT_MONTH_NAMES[date.getMonth()] ?? ''
  if (granularity === 'monthly') {
    return `${month} ${String(date.getFullYear() % 100).padStart(2, '0')}`
  }
  return `${date.getDate()} ${month}`
}

function formatLabel(value: string | number | null): string {
  if (isDateLike(value)) return formatChartDateKey(value)
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return String(value)
  return value
}

export function formatFieldDisplayName(value: string): string {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''
  const bracketMatch = trimmed.match(/^(?:'[^']+'|[^[]*)\[([^\]]+)\]$/)
  const source = (bracketMatch?.[1] ?? trimmed).replace(/^'+|'+$/g, '')

  return source
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((token) => {
      const upper = token.toUpperCase()
      if (DISPLAY_ACRONYMS.has(upper) || (/^[A-Z0-9%]+$/.test(token) && token.length <= 5)) {
        return upper
      }
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase()
    })
    .join(' ')
}

export function formatFieldReferencesInText(value: string): string {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''
  return trimmed
    .replace(/(?:'[^']+'|[A-Za-z0-9_ ]+)\[([^\]]+)\]\s+\1\b/g, (_, field: string) => formatFieldDisplayName(field))
    .replace(/'[^']+'\[([^\]]+)\]/g, (_, field: string) => formatFieldDisplayName(field))
    .replace(/\b[A-Za-z0-9_ ]+\[([^\]]+)\]/g, (_, field: string) => formatFieldDisplayName(field))
    .replace(/\[([^\]]+)\]/g, (_, field: string) => formatFieldDisplayName(field))
    .replace(/\b([A-Za-z][A-Za-z0-9 %]+)\s+\1\b/gi, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

export type MetricFormatOptions = {
  /** Overrides the format's decimals (or the default's). */
  decimals?: number
  /** Stored/resolved ReportBuilderOptions format; when absent the measure-name default is used. */
  format?: FieldFormat | null
  /** Chart axis ticks: no decimals. */
  compact?: boolean
}

/**
 * Formats a measure value. Uses the ReportBuilderOptions format when one is given, otherwise the
 * measure-name default (the same rule the .NET export applies to fields without a stored format).
 */
export function formatMetricValue(value: string | number | null | undefined, metricName?: string | null, options?: MetricFormatOptions): string {
  const base = options?.format ?? resolveDefaultFieldFormat(metricName, 'number', true)
  const format = options?.decimals !== undefined ? { ...base, decimals: options.decimals } : base
  return formatFieldValue(value, format, { compact: options?.compact })
}

/** Looks up a returned column's format from the server-resolved columnFormats map. */
export function resolveColumnFormat(columnFormats: Record<string, FieldFormat> | null | undefined, column: string | null | undefined): FieldFormat | null {
  return resolveFieldFormat(column, { columnFormats })
}

/** Formats a cell/series value for a column using its ReportBuilderOptions format when known. */
export function formatColumnValue(
  value: string | number | null | undefined,
  column: string | null | undefined,
  columnFormats: Record<string, FieldFormat> | null | undefined,
  options?: { compact?: boolean },
): string {
  return formatMetricValue(value, column, { format: resolveColumnFormat(columnFormats, column), compact: options?.compact })
}

const COMPACT_UNITS = [
  { threshold: 1_000_000_000, suffix: 'B', digits: 2 },
  { threshold: 1_000_000, suffix: 'M', digits: 2 },
  { threshold: 1_000, suffix: 'K', digits: 1 },
]

function formatTrimmed(value: number, digits: number) {
  return new Intl.NumberFormat('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: digits }).format(value)
}

/** 1_037_346 -> "1.04M", 212_654 -> "212.7K", 450 -> "450". Rounding up to 1000 of a unit rolls into the next one (999_960 -> "1M"). */
function abbreviateMagnitude(magnitude: number, smallDigits: number): string {
  for (const [index, unit] of COMPACT_UNITS.entries()) {
    if (magnitude < unit.threshold) continue
    const scaled = roundHalfUp(magnitude / unit.threshold, unit.digits)
    const bigger = COMPACT_UNITS[index - 1]
    if (scaled >= 1000 && bigger) {
      return `${formatTrimmed(roundHalfUp(magnitude / bigger.threshold, bigger.digits), bigger.digits)}${bigger.suffix}`
    }
    return `${formatTrimmed(scaled, unit.digits)}${unit.suffix}`
  }
  return formatTrimmed(roundHalfUp(magnitude, smallDigits), smallDigits)
}

/**
 * Short form of formatColumnValue for headline numbers, scale labels and badges: $1.04M, $212.7K, 83.2%.
 * It keeps the column's stored kind and currency symbol but not its decimals (the exact figure is
 * shown elsewhere); a column with no stored format falls back to the measure-name default.
 * Millions and billions keep up to two decimals, thousands one, percentages one.
 */
export function formatCompactColumnValue(
  value: string | number | null | undefined,
  column: string | null | undefined,
  columnFormats: Record<string, FieldFormat> | null | undefined,
): string {
  if (value === null || value === undefined || value === '') return '-'
  const format = resolveColumnFormat(columnFormats, column) ?? resolveDefaultFieldFormat(column, 'number', true)
  if (!isNumericFormatKind(format.kind)) return formatColumnValue(value, column, columnFormats)
  const numeric = typeof value === 'number' ? value : Number(value.replace(/[$,\s]/g, ''))
  if (!Number.isFinite(numeric)) return String(value)

  if (format.kind === 'percent') {
    const percent = roundHalfUp(numeric * 100, 1)
    return `${formatTrimmed(percent, 1)}%`
  }

  // Whole units below 1,000 for money and whole numbers; plain numbers keep up to two decimals.
  const amount = abbreviateMagnitude(Math.abs(numeric), format.kind === 'number' ? 2 : 0)
  const symbol = format.kind === 'currency' ? format.currencySymbol || '$' : ''
  return `${numeric < 0 && amount !== '0' ? '-' : ''}${symbol}${amount}`
}

/**
 * Formats a chart category label (dimension value) with the dimension's ReportBuilderOptions format.
 * Display only: callers keep the raw label for drilldown. Labels without a known format are returned unchanged.
 */
export function formatDimensionLabel(
  value: string | number | null | undefined,
  column: string | null | undefined,
  columnFormats: Record<string, FieldFormat> | null | undefined,
): string {
  const text = value === null || value === undefined ? '' : String(value)
  const format = resolveColumnFormat(columnFormats, column)
  if (!format) return isDateLike(text) ? formatDateTimeLabel(text) : text
  if (!text || format.kind === 'text') return text
  if (isDateFormatKind(format.kind)) {
    // ISO strings keep their time part; pre-formatted slash labels go through the dashboard date parser.
    if (ISO_DATE_PATTERN.test(text)) return formatFieldValue(text, format)
    const date = parseDashboardDate(text)
    return date ? formatFieldValue(date.getTime(), format) : text
  }
  return formatFieldValue(text, format)
}

/** The dimension a chart's category labels come from: the widget's first dimension, else the best label column. */
export function resolveChartDimension(dimensions: string[] | null | undefined, dimensionCols: string[], rows: DataRow[]): string | null {
  return dimensions?.[0] ?? pickLabelColumn(dimensionCols, rows)
}

/**
 * Formats a table cell. Columns with a stored/resolved format use it (numbers, dates, text IDs);
 * anything unknown keeps the existing behaviour (measure-name default for numbers, date-like strings as DD/MM/YY).
 */
export function formatTableCellValue(value: unknown, column: string, columnFormats: Record<string, FieldFormat> | null | undefined): string {
  if (value === null || value === undefined) return '-'
  const format = resolveColumnFormat(columnFormats, column)
  if (typeof value === 'number') return format?.kind === 'text' ? String(value) : formatMetricValue(value, column, { format })
  const text = String(value)
  if (format && format.kind !== 'text') return formatFieldValue(text, format)
  return formatFieldReferencesInText(formatTableCellString(text))
}

export type InsightValueFormat = 'currency' | 'percent' | 'number'

/** Resolves how a measure renders on screen: ReportBuilderOptions format first, then widget format, then the name default. */
export function resolveInsightValueFormat(metricName: string, explicit?: DashboardValueFormat['type'], fieldFormat?: FieldFormat | null): InsightValueFormat {
  const stored = toInsightValueFormat(fieldFormat)
  if (stored) return stored
  if (explicit === 'currency' || explicit === 'percent') return explicit
  if (explicit === 'integer' || explicit === 'number') return 'number'
  return resolveMeasureKindFromName(metricName)
}

/** Formats a value the way the dashboard displays it, so insight text quotes the same figures users see. */
export function formatInsightValue(value: string | number | null | undefined, format: InsightValueFormat): string {
  if (typeof value === 'string' && !Number.isFinite(Number(value.replace(/,/g, '')))) return value
  return formatFieldValue(value, { kind: format })
}

export function deriveChartData(columns: string[], rows: DataRow[]): AgentBuilderChartDataPoint[] {
  if (!rows.length || !columns.length) return []
  const { measureCols, dimensionCols } = classifyColumns(columns, rows)
  if (!measureCols.length) return []
  const labelCol = pickLabelColumn(dimensionCols, rows)
  if (!labelCol) return []
  const primaryMeasure = measureCols[0]
  if (!primaryMeasure) return []
  return rows.map((row) => ({
    label: formatLabel(row[labelCol] ?? null),
    value: typeof row[primaryMeasure] === 'number' ? (row[primaryMeasure] as number) : 0,
    series: measureCols.map((m) => ({
      name: m,
      value: typeof row[m] === 'number' ? (row[m] as number) : 0,
    })),
  }))
}

export function deriveChartDataForDimension(columns: string[], rows: DataRow[], labelCol: string, primaryMeasureOverride?: string | null): AgentBuilderChartDataPoint[] {
  if (!rows.length || !columns.length) return []
  const { measureCols } = classifyColumns(columns, rows)
  if (!measureCols.length) return []
  const resolvedPrimary = primaryMeasureOverride && measureCols.includes(primaryMeasureOverride) ? primaryMeasureOverride : measureCols[0]
  if (!resolvedPrimary) return []
  const accumMap = new Map<string, { value: number; series: Map<string, number> }>()
  for (const row of rows) {
    const label = formatLabel(row[labelCol] ?? null)
    let acc = accumMap.get(label)
    if (!acc) {
      acc = { value: 0, series: new Map(measureCols.map((m) => [m, 0])) }
      accumMap.set(label, acc)
    }
    acc.value += typeof row[resolvedPrimary] === 'number' ? (row[resolvedPrimary] as number) : 0
    for (const m of measureCols) {
      acc.series.set(m, (acc.series.get(m) ?? 0) + (typeof row[m] === 'number' ? (row[m] as number) : 0))
    }
  }
  return Array.from(accumMap.entries()).map(([label, acc]) => ({
    label,
    value: acc.value,
    series: Array.from(acc.series.entries()).map(([name, value]) => ({ name, value })),
  }))
}

export function deriveKpiCards(columns: string[], rows: DataRow[]): AgentBuilderKpiCard[] {
  if (!rows.length || !columns.length) return []
  const { measureCols, dimensionCols } = classifyColumns(columns, rows)
  if (!measureCols.length) return []

  if (rows.length === 1 && measureCols.length > 1) {
    const row = rows[0]
    if (!row) return []

    return measureCols.map((measure) => ({
      label: formatFieldDisplayName(measure),
      value: typeof row[measure] === 'number' ? (row[measure] as number) : row[measure] ?? '',
      measureLabel: formatFieldDisplayName(measure),
      measureKey: measure,
    }))
  }

  const labelCol = pickLabelColumn(dimensionCols, rows)
  const primaryMeasure = measureCols[0]
  if (!primaryMeasure) return []
  const measureLabel = formatFieldDisplayName(primaryMeasure)
  return rows.map((row) => ({
    label: labelCol ? formatLabel(row[labelCol] ?? null) : '',
    value: typeof row[primaryMeasure] === 'number' ? (row[primaryMeasure] as number) : row[primaryMeasure] ?? '',
    measureLabel,
    measureKey: primaryMeasure,
  }))
}

export function getBarAxisMin(values: number[]): number {
  const finiteValues = values.filter(Number.isFinite)
  if (!finiteValues.length) return 0
  const minValue = Math.min(...finiteValues)
  return minValue < 0 ? minValue : 0
}

export function getBarAxisMaxFallback(values: number[]): number | 'dataMax' {
  const finiteValues = values.filter(Number.isFinite)
  if (!finiteValues.length) return 'dataMax'
  return Math.max(...finiteValues) < 0 ? 0 : 'dataMax'
}
